import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../../api/client";
import type { FrameRow, Me, RoadDetail } from "../../api/types";
import { imageUrl } from "../../components/EvidenceViewer";
import { ConfirmDialog, Dialog, useToast } from "../../components/Feedback";
import { Icon } from "../../components/Icon";
import { Field, Panel } from "../../components/Layout";
import { EmptyState, QueryState, SkeletonBlock } from "../../components/States";
import { can } from "../../app/auth";
import { chainage, dateTime } from "../../lib/format";

const PAGE = 120;

export function ImagesTab({ road, me }: { road: RoadDetail; me: Me }) {
  const sid = road.survey.survey_uuid;
  const reviewer = can.review(me.role);
  const qc = useQueryClient();
  const toast = useToast();
  const frames = useQuery({ queryKey: ["road", sid, "frames"], queryFn: () => api<{ items: FrameRow[] }>(`/api/roads/${sid}/frames`) });
  const [selecting, setSelecting] = useState(false);
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [confirm, setConfirm] = useState(false);
  const [reason, setReason] = useState("");
  const [shown, setShown] = useState(PAGE);
  const [viewing, setViewing] = useState<FrameRow | null>(null);

  const del = useMutation({
    mutationFn: () => api<{ removed: number }>(`/api/roads/${sid}/frames/delete`, { method: "POST", json: { frame_uuids: [...picked], reason } }),
    onSuccess: (r) => {
      toast({ text: `${r.removed} image${r.removed === 1 ? "" : "s"} removed from review and reports.` });
      setPicked(new Set()); setSelecting(false); setConfirm(false); setReason("");
      qc.invalidateQueries({ queryKey: ["road", sid] });
    },
    onError: (e) => toast({ text: e instanceof Error ? e.message : "Images could not be removed.", tone: "error" }),
  });

  const toggle = (id: string) => setPicked((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });

  return (
    <>
      {road.has_video ? (
        <Panel title="Detection video" id="img-video">
          <video controls preload="metadata" style={{ width: "100%", maxHeight: 420, background: "var(--navy-ink)", borderRadius: 8 }} src={`/api/roads/${sid}/video`}>
            Your browser can't play this video.
          </video>
        </Panel>
      ) : null}

      <Panel
        title="Captured images"
        id="img-grid"
        actions={reviewer ? (
          selecting ? (
            <div className="row">
              <span className="muted">{picked.size} selected</span>
              <button className="btn btn-sm" onClick={() => { setSelecting(false); setPicked(new Set()); }}>Cancel</button>
              <button className="btn btn-sm btn-danger" disabled={!picked.size} onClick={() => setConfirm(true)}><Icon name="trash" />Remove selected</button>
            </div>
          ) : <button className="btn btn-sm" onClick={() => setSelecting(true)}>Select irrelevant images</button>
        ) : null}
      >
        <QueryState query={frames} skeleton={<SkeletonBlock rows={5} height={90} />}>
          {(d) => d.items.length ? (
            <>
              <p className="muted" style={{ marginBottom: 12, fontSize: 13 }}>
                {d.items.length} images in chainage order.{reviewer ? "" : " Only images that have been privacy-blurred are shown."}
              </p>
              <div className="image-grid">
                {d.items.slice(0, shown).map((f) => {
                  const sel = picked.has(f.frame_uuid);
                  return (
                    <button key={f.frame_uuid} className={`image-card${sel ? " selected" : ""}`} aria-pressed={selecting ? sel : undefined}
                            onClick={() => (selecting ? toggle(f.frame_uuid) : setViewing(f))}
                            aria-label={`${selecting ? (sel ? "Deselect" : "Select") : "Open"} image at ${chainage(f.chainage_m)}`}>
                      <img src={imageUrl(f.frame_uuid, true)} alt="" loading="lazy" />
                      {selecting ? <span className="tick" aria-hidden="true">{sel ? <Icon name="check" size={14} /> : null}</span> : null}
                      <span className="meta"><span>{chainage(f.chainage_m)}</span>{f.detections ? <span>{f.detections} AI</span> : null}</span>
                    </button>
                  );
                })}
              </div>
              {shown < d.items.length ? (
                <div className="row" style={{ justifyContent: "center", marginTop: 16 }}>
                  <button className="btn" onClick={() => setShown((n) => n + PAGE)}>Show {Math.min(PAGE, d.items.length - shown)} more</button>
                </div>
              ) : null}
            </>
          ) : <EmptyState title="No images for this survey" />}
        </QueryState>
      </Panel>

      <ConfirmDialog
        open={confirm}
        title={`Remove ${picked.size} image${picked.size === 1 ? "" : "s"}?`}
        danger
        confirmLabel="Remove images"
        busy={del.isPending || reason.trim().length < 3}
        onCancel={() => setConfirm(false)}
        onConfirm={() => del.mutate()}
        body={
          <div className="stack">
            <p className="muted">They'll be taken out of review, the map and reports, along with any AI findings on them. The files stay on the server and the removal is logged.</p>
            <Field label="Reason" htmlFor="del-reason" hint="For example: dashboard only, blurred, car park.">
              <textarea id="del-reason" className="textarea" value={reason} onChange={(e) => setReason(e.target.value)} />
            </Field>
          </div>
        }
      />

      <Dialog open={!!viewing} onClose={() => setViewing(null)} labelledBy="img-view-title">
        {viewing ? (
          <div className="dialog-body" style={{ width: "min(960px, calc(100vw - 32px))" }}>
            <div className="row"><h2 className="dialog-title" id="img-view-title">{chainage(viewing.chainage_m)}</h2><span className="spacer" /><button className="btn btn-ghost btn-sm" onClick={() => setViewing(null)}>Close</button></div>
            <img src={imageUrl(viewing.frame_uuid)} alt={`Road image at ${chainage(viewing.chainage_m)}`} style={{ borderRadius: 8 }} />
            <p className="muted">Captured {dateTime(viewing.captured_at_device)}</p>
          </div>
        ) : null}
      </Dialog>
    </>
  );
}
